import { relations } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'

import { user } from '../auth'
import { NotificationCategoryEnum, NotificationChannelEnum, NotificationDeliveryStatusEnum } from '../enums'
import { Notification } from './event'

export const NotificationDelivery = pgTable(
  'notification_delivery',
  {
    id: uuid().primaryKey().defaultRandom(),
    notificationId: uuid()
      .notNull()
      .references(() => Notification.id, { onDelete: 'cascade' }),
    channel: NotificationChannelEnum().notNull(),
    status: NotificationDeliveryStatusEnum().notNull().default('PENDING'),
    /**
     * Delivery target discriminator: 'self', 'guardian:<userId>', or a push endpoint id.
     * Part of the unique key so one notification can fan out to multiple guardians.
     */
    target: text().notNull().default('self'),
    attempts: integer().notNull().default(0),
    lastError: text(),
    sentAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    index('idx_notification_delivery_notification').on(table.notificationId),
    index('idx_notification_delivery_status').on(table.status),
    unique('uq_notification_delivery').on(table.notificationId, table.channel, table.target),
  ],
)

export const PushSubscription = pgTable(
  'push_subscription',
  {
    id: uuid().primaryKey().defaultRandom(),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    endpoint: text().notNull().unique(),
    p256dh: text().notNull(),
    auth: text().notNull(),
    userAgent: text(),
    failureCount: integer().notNull().default(0),
    lastSeenAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [index('idx_push_subscription_user').on(table.userId)],
)

export const NotificationPreference = pgTable(
  'notification_preference',
  {
    id: uuid().primaryKey().defaultRandom(),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    category: NotificationCategoryEnum().notNull(),
    channel: NotificationChannelEnum().notNull(),
    enabled: boolean().notNull().default(true),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [unique('uq_notification_preference').on(table.userId, table.category, table.channel)],
)

export const notificationDeliveryRelations = relations(NotificationDelivery, ({ one }) => ({
  notification: one(Notification, {
    fields: [NotificationDelivery.notificationId],
    references: [Notification.id],
  }),
}))

export const pushSubscriptionRelations = relations(PushSubscription, ({ one }) => ({
  user: one(user, { fields: [PushSubscription.userId], references: [user.id] }),
}))

export const notificationPreferenceRelations = relations(NotificationPreference, ({ one }) => ({
  user: one(user, { fields: [NotificationPreference.userId], references: [user.id] }),
}))

export const InsertNotificationDeliverySchema = createInsertSchema(NotificationDelivery).omit({
  id: true,
  createdAt: true,
})
export const SelectNotificationDeliverySchema = createSelectSchema(NotificationDelivery)

export const InsertPushSubscriptionSchema = createInsertSchema(PushSubscription).omit({ id: true, createdAt: true })
export const SelectPushSubscriptionSchema = createSelectSchema(PushSubscription)

export const InsertNotificationPreferenceSchema = createInsertSchema(NotificationPreference).omit({
  id: true,
  createdAt: true,
})
export const SelectNotificationPreferenceSchema = createSelectSchema(NotificationPreference)

export type InsertNotificationDelivery = typeof NotificationDelivery.$inferInsert
export type SelectNotificationDelivery = typeof NotificationDelivery.$inferSelect
export type InsertPushSubscription = typeof PushSubscription.$inferInsert
export type SelectPushSubscription = typeof PushSubscription.$inferSelect
export type InsertNotificationPreference = typeof NotificationPreference.$inferInsert
export type SelectNotificationPreference = typeof NotificationPreference.$inferSelect
